import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Switch from "react-switch";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../sessionSlice";
import { fetchThemeData } from "../../themeSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { apiUrl } from "../../../utils/apiUrl";
import { clearAdminDirty, setAdminDirty } from "../../adminDirty";
import "./juryMembers.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LOCAL_PHONE_RE = /^\d{6,14}$/;
const JURY_LOGIN_METHOD_OPTIONS = [
  { value: "employee_id", label: "Employee ID" },
  { value: "email", label: "Email" },
];

const COUNTRY_CODES = [
  { code: "+91", country: "India", flag: "🇮🇳" },
  { code: "+1", country: "USA / Canada", flag: "🇺🇸" },
  { code: "+44", country: "UK", flag: "🇬🇧" },
  { code: "+61", country: "Australia", flag: "🇦🇺" },
  { code: "+971", country: "UAE", flag: "🇦🇪" },
  { code: "+65", country: "Singapore", flag: "🇸🇬" },
  { code: "+81", country: "Japan", flag: "🇯🇵" },
  { code: "+49", country: "Germany", flag: "🇩🇪" },
  { code: "+33", country: "France", flag: "🇫🇷" },
  { code: "+86", country: "China", flag: "🇨🇳" },
];

function parseStoredPhone(raw) {
  const s = String(raw || "").trim().replace(/\s+/g, "");
  if (!s) return { countryCode: "+91", local: "" };
  const match = COUNTRY_CODES.find((c) => s.startsWith(c.code));
  if (match) {
    return { countryCode: match.code, local: s.slice(match.code.length).replace(/\D/g, "") };
  }
  if (/^\d{10}$/.test(s)) return { countryCode: "+91", local: s };
  const plus = s.match(/^(\+\d{1,4})(\d+)$/);
  if (plus) return { countryCode: plus[1], local: plus[2] };
  return { countryCode: "+91", local: s.replace(/\D/g, "") };
}

function formatPhone(countryCode, local) {
  const digits = String(local || "").replace(/\D/g, "");
  if (!digits) return "";
  return `${countryCode}${digits}`;
}

const emptyForm = () => ({
  jury_member_name: "",
  jury_member_email: "",
  jury_member_phone: "",
  phoneCountryCode: "+91",
  phoneLocal: "",
  employee_id: "",
});

const emptyErrors = () => ({
  jury_member_name: "",
  jury_member_email: "",
  jury_member_phone: "",
  employee_id: "",
});

function parsePercentage(raw) {
  const s = String(raw ?? "").trim().replace(/%/g, "");
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  if (n < 0 || n > 100) return null;
  return n;
}

function normalizePhoneKey(raw) {
  return String(raw || "").replace(/\s+/g, "").replace(/^\+/, "+");
}

function normalizeJuryLoginMethod(raw) {
  return String(raw || "").trim().toLowerCase() === "email" ? "email" : "employee_id";
}

function validateForm(form, juryLoginMethod) {
  const errors = emptyErrors();
  const email = form.jury_member_email.trim();
  const local = String(form.phoneLocal || "").replace(/\D/g, "");
  const employeeId = form.employee_id.trim();

  if (juryLoginMethod === "email" && !email) {
    errors.jury_member_email = "Email is required.";
  } else if (email && !EMAIL_RE.test(email)) {
    errors.jury_member_email = "Enter a valid email address.";
  }

  if (local) {
    if (form.phoneCountryCode === "+91") {
      if (!/^\d{10}$/.test(local)) {
        errors.jury_member_phone = "Indian phone numbers must be exactly 10 digits.";
      }
    } else if (!LOCAL_PHONE_RE.test(local)) {
      errors.jury_member_phone = "Enter a valid phone number (6–14 digits).";
    }
  }

  if (juryLoginMethod === "employee_id" && !employeeId) {
    errors.employee_id = "Employee ID is required.";
  }

  return errors;
}

function findDuplicateMember(members, form, editingId, phone) {
  const email = form.jury_member_email.trim().toLowerCase();
  const emp = form.employee_id.trim().toLowerCase();
  const phoneKey = phone ? normalizePhoneKey(phone) : "";
  return (members || []).find((m) => {
    if (editingId && Number(m.id) === Number(editingId)) return false;
    const mEmail = String(m.jury_member_email || "").trim().toLowerCase();
    const mEmp = String(m.employee_id || "").trim().toLowerCase();
    const mPhone = normalizePhoneKey(m.jury_member_phone);
    if (email && mEmail === email) return true;
    if (emp && mEmp === emp) return true;
    if (phoneKey && mPhone && mPhone === phoneKey) return true;
    return false;
  });
}

function displayPhone(raw) {
  const { countryCode, local } = parseStoredPhone(raw);
  if (!local) return "—";
  return `${countryCode} ${local}`;
}

export default function JuryMembers() {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((s) => s.theme);

  const [members, setMembers] = useState([]);
  const [maxMembers, setMaxMembers] = useState(10);
  const [maxPoints, setMaxPoints] = useState(100);
  const [percentage, setPercentage] = useState("70%");
  const [addEnabled, setAddEnabled] = useState(false);
  const [juryLoginMethod, setJuryLoginMethod] = useState("employee_id");
  const [leaderboardDisplay, setLeaderboardDisplay] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState(emptyErrors());
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    return () => clearAdminDirty();
  }, []);

  const markDirty = useCallback(() => setAdminDirty(true, "Jury"), []);

  const loadMembers = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    try {
      const resp = await fetch(apiUrl("/adminJuryMembers"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ action: "list" }),
      });
      const json = await resp.json();
      if (resp.ok && json.success) {
        setMembers(Array.isArray(json.members) ? json.members : []);
        setMaxMembers(json.maxMembers || 10);
        setMaxPoints(json.juryMaxPoints || 100);
        setPercentage(json.juryPointsPercentage || "70%");
        setAddEnabled(String(json.addJuryMembers) === "true");
        setJuryLoginMethod(normalizeJuryLoginMethod(json.juryLoginMethod));
        if (typeof json.leaderboardDisplay === "boolean") {
          setLeaderboardDisplay(json.leaderboardDisplay);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (currentTheme) {
      dispatch(fetchThemeData({ themeId: currentTheme }));
    }
  }, [dispatch, currentTheme]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    if (data && "leaderboard_display" in data) {
      setLeaderboardDisplay(
        String(data.leaderboard_display ?? "false").trim().toLowerCase() === "true"
      );
    }
  }, [data?.leaderboard_display]);

  const persistThemeSettings = async (overrides = {}) => {
    if (!currentTheme || !adminToken) return;

    const nextMax = Number(overrides.maxMembers ?? maxMembers);
    if (Number.isFinite(nextMax) && nextMax < members.length) {
      Swal.fire(
        "Validation",
        `You have ${members.length} jury members. Delete members first before lowering the max below ${members.length}.`,
        "warning"
      );
      return;
    }

    const pctNum = parsePercentage(overrides.percentage ?? percentage);
    if (pctNum === null) {
      Swal.fire(
        "Validation",
        "Jury points percentage must be a whole number from 0–100 (e.g. 70%), not a decimal.",
        "warning"
      );
      return;
    }

    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          currentTheme,
          data: {
            add_jury_members: (overrides.addEnabled ?? addEnabled) ? "true" : "false",
            jury_login_method: normalizeJuryLoginMethod(
              overrides.juryLoginMethod ?? juryLoginMethod
            ),
            jury_max_members: nextMax || 10,
            jury_max_points: Number(overrides.maxPoints ?? maxPoints) || 100,
            jury_points_percentage: `${pctNum}%`,
            leaderboard_display:
              (overrides.leaderboardDisplay ?? leaderboardDisplay) ? "true" : "false",
          },
        },
        token: adminToken,
      });
      setPercentage(`${pctNum}%`);
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      clearAdminDirty();
      Swal.fire("Saved", "Jury settings updated.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Could not save settings.", "error");
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    markDirty();
    if (errors[key]) {
      setErrors((p) => ({ ...p, [key]: "" }));
    }
  };

  const saveMember = async () => {
    const nextErrors = validateForm(form, juryLoginMethod);
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      const first = Object.values(nextErrors).find(Boolean);
      Swal.fire("Validation", first, "warning");
      return;
    }
    const phone = formatPhone(form.phoneCountryCode, form.phoneLocal);
    const dup = findDuplicateMember(members, form, editingId, phone);
    if (dup) {
      const email = form.jury_member_email.trim().toLowerCase();
      const emp = form.employee_id.trim().toLowerCase();
      const phoneKey = phone ? normalizePhoneKey(phone) : "";
      let reason = "email, phone, or employee ID";
      if (String(dup.jury_member_email || "").trim().toLowerCase() === email) {
        reason = "email";
      } else if (String(dup.employee_id || "").trim().toLowerCase() === emp) {
        reason = "employee ID";
      } else if (phoneKey && normalizePhoneKey(dup.jury_member_phone) === phoneKey) {
        reason = "phone number";
      }
      Swal.fire(
        "Duplicate jury member",
        `A jury member with this ${reason} already exists.`,
        "warning"
      );
      return;
    }
    try {
      const resp = await fetch(apiUrl("/adminJuryMembers"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          action: editingId ? "update" : "create",
          member: {
            jury_member_name: form.jury_member_name,
            jury_member_email: form.jury_member_email,
            jury_member_phone: phone,
            employee_id: form.employee_id,
            id: editingId,
          },
        }),
      });
      const json = await resp.json();
      if (!resp.ok || !json.success) {
        throw new Error(json.error || "Save failed");
      }
      setForm(emptyForm());
      setErrors(emptyErrors());
      setEditingId(null);
      await loadMembers();
      clearAdminDirty();
      Swal.fire("Saved", "Jury member saved.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Save failed", "error");
    }
  };

  const deleteMember = async (id) => {
    const ok = await Swal.fire({
      title: "Delete member?",
      icon: "warning",
      showCancelButton: true,
    });
    if (!ok.isConfirmed) return;
    try {
      const resp = await fetch(apiUrl("/adminJuryMembers"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ action: "delete", member: { id } }),
      });
      const json = await resp.json();
      if (!resp.ok || !json.success) throw new Error(json.error || "Delete failed");
      await loadMembers();
    } catch (e) {
      Swal.fire("Error", e.message || "Delete failed", "error");
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setErrors(emptyErrors());
  };

  const startEdit = (m) => {
    const parsed = parseStoredPhone(m.jury_member_phone);
    setEditingId(m.id);
    setForm({
      jury_member_name: m.jury_member_name || "",
      jury_member_email: m.jury_member_email || "",
      jury_member_phone: m.jury_member_phone || "",
      phoneCountryCode: parsed.countryCode,
      phoneLocal: parsed.local,
      employee_id: m.employee_id || "",
    });
    setErrors(emptyErrors());
  };

  return (
    <div className="jury-admin-page" data-tour="jury-page">
      <div className="admin-tab-container jury-admin-panel">
        <div className="admin-tab-title">
          <i className="fa-solid fa-gavel" /> &nbsp; Jury members
        </div>

        <div className="jury-settings-row">
          <label>Enable jury</label>
          <Switch
            checked={addEnabled}
            onChange={async (checked) => {
              setAddEnabled(checked);
              await persistThemeSettings({ addEnabled: checked });
            }}
            onColor="#7c3aed"
            offColor="#cbd5e1"
            height={22}
            width={44}
          />
        </div>

        <div className="jury-settings-row">
          <label>Show leaderboard to players</label>
          <Switch
            checked={leaderboardDisplay}
            onChange={async (checked) => {
              setLeaderboardDisplay(checked);
              await persistThemeSettings({ leaderboardDisplay: checked });
            }}
            onColor="#7c3aed"
            offColor="#cbd5e1"
            height={22}
            width={44}
            disabled={saving}
          />
        </div>
        <div className="code" style={{ marginBottom: 16 }}>
          When on, players see a Leaderboard button on the talent gallery.
        </div>
        <div className="form-group-v1 jury-login-method">
          <label>Jury login with</label>
          <div className="jury-login-method-options">
            {JURY_LOGIN_METHOD_OPTIONS.map((opt) => (
              <label key={opt.value} className="jury-login-method-option">
                <input
                  type="radio"
                  name="jury-login-method"
                  value={opt.value}
                  checked={juryLoginMethod === opt.value}
                  onChange={async (e) => {
                    const nextMethod = normalizeJuryLoginMethod(e.target.value);
                    setJuryLoginMethod(nextMethod);
                    markDirty();
                    await persistThemeSettings({ juryLoginMethod: nextMethod });
                  }}
                  disabled={saving}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
          <div className="code">
            Jury members will sign in using only the selected identifier.
          </div>
        </div>

        <div className="form-group-v1" data-tour="jury-max-members">
          <label>Max jury members</label>
          <input
            type="number"
            min={1}
            max={50}
            value={maxMembers}
            onChange={(e) => {
              setMaxMembers(e.target.value);
              markDirty();
            }}
          />
        </div>
        <div className="form-group-v1" data-tour="jury-max-points">
          <label>Max points per entry</label>
          <input
            type="number"
            min={1}
            max={1000}
            value={maxPoints}
            onChange={(e) => {
              setMaxPoints(e.target.value);
              markDirty();
            }}
          />
        </div>
        <div className="form-group-v1" data-tour="jury-percentage">
          <label>Jury points percentage</label>
          <input
            type="text"
            inputMode="numeric"
            value={percentage}
            onChange={(e) => {
              const raw = e.target.value;
              const hasPercent = raw.includes("%");
              const digits = raw.replace(/[^\d]/g, "").slice(0, 3);
              setPercentage(hasPercent && digits ? `${digits}%` : digits || (hasPercent ? "%" : ""));
              markDirty();
            }}
            placeholder="70%"
          />
          <div className="code">
            Final score = jury average × this % + public likes × remainder.
          </div>
        </div>
        <div className="jury-actions" style={{ marginBottom: 20 }}>
          <button
            type="button"
            className="save-and-continue"
            disabled={saving}
            onClick={() => persistThemeSettings()}
            data-tour="jury-save-settings"
          >
            {saving ? "Saving…" : "Save jury settings"}
          </button>
        </div>

        <h3 className="custom-rules-title">
          {editingId ? "Edit member" : "Add member"} ({members.length}/{maxMembers})
        </h3>

        <div className="jury-member-form" data-tour="jury-add-member">
          <div className={`form-group-v1${errors.jury_member_name ? " has-error" : ""}`}>
            <label htmlFor="jury-name">Name</label>
            <input
              id="jury-name"
              type="text"
              placeholder="Full name"
              value={form.jury_member_name}
              onChange={(e) => setField("jury_member_name", e.target.value)}
              autoComplete="name"
            />
            {errors.jury_member_name ? (
              <div className="jury-field-error">{errors.jury_member_name}</div>
            ) : null}
          </div>

          <div className={`form-group-v1${errors.jury_member_email ? " has-error" : ""}`}>
            <label htmlFor="jury-email">
              Email {juryLoginMethod === "email" ? "*" : "(optional)"}
            </label>
            <input
              id="jury-email"
              type="email"
              placeholder="name@company.com"
              value={form.jury_member_email}
              onChange={(e) => setField("jury_member_email", e.target.value)}
              autoComplete="email"
              required={juryLoginMethod === "email"}
            />
            {errors.jury_member_email ? (
              <div className="jury-field-error">{errors.jury_member_email}</div>
            ) : null}
          </div>

          <div className={`form-group-v1${errors.jury_member_phone ? " has-error" : ""}`}>
            <label htmlFor="jury-phone">Phone</label>
            <div className="jury-phone-row">
              <select
                className="jury-country-code"
                value={form.phoneCountryCode}
                onChange={(e) => {
                  const code = e.target.value;
                  setForm((p) => ({
                    ...p,
                    phoneCountryCode: code,
                    phoneLocal:
                      code === "+91"
                        ? String(p.phoneLocal || "").replace(/\D/g, "").slice(0, 10)
                        : p.phoneLocal,
                  }));
                  markDirty();
                  if (errors.jury_member_phone) {
                    setErrors((p) => ({ ...p, jury_member_phone: "" }));
                  }
                }}
                aria-label="Country code"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.code} ({c.country})
                  </option>
                ))}
              </select>
              <input
                id="jury-phone"
                type="tel"
                inputMode="numeric"
                maxLength={form.phoneCountryCode === "+91" ? 10 : 14}
                placeholder="e.g. 9876543210"
                value={form.phoneLocal}
                onChange={(e) => {
                  const maxLen = form.phoneCountryCode === "+91" ? 10 : 14;
                  const digits = e.target.value.replace(/\D/g, "").slice(0, maxLen);
                  setField("phoneLocal", digits);
                }}
                autoComplete="tel-national"
              />
            </div>
            {errors.jury_member_phone ? (
              <div className="jury-field-error">{errors.jury_member_phone}</div>
            ) : (
              <div className="jury-field-hint">
                Format: {form.phoneCountryCode}{" "}
                {form.phoneLocal ||
                  (form.phoneCountryCode === "+91" ? "xxxxxxxxxx" : "xxxxxx…")}
              </div>
            )}
          </div>

          <div className={`form-group-v1${errors.employee_id ? " has-error" : ""}`}>
            <label htmlFor="jury-employee-id">
              Employee ID {juryLoginMethod === "employee_id" ? "*" : "(optional)"}
            </label>
            <input
              id="jury-employee-id"
              type="text"
              placeholder="Employee ID"
              value={form.employee_id}
              onChange={(e) => setField("employee_id", e.target.value)}
              required={juryLoginMethod === "employee_id"}
            />
            {errors.employee_id ? (
              <div className="jury-field-error">{errors.employee_id}</div>
            ) : null}
          </div>
        </div>

        <div className="jury-actions">
          <button type="button" className="save-and-continue" onClick={saveMember} disabled={saving}>
            {editingId ? "Update member" : "Add member"}
          </button>
          {editingId ? (
            <button type="button" className="add-btn" onClick={resetForm}>
              Cancel edit
            </button>
          ) : null}
        </div>

        {loading ? (
          <p>Loading…</p>
        ) : (
          <table className="jury-table" data-tour="jury-list">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Employee ID</th>
                <th>Phone</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id}>
                  <td>{m.jury_member_name || "—"}</td>
                  <td>{m.jury_member_email}</td>
                  <td>{m.employee_id}</td>
                  <td>{displayPhone(m.jury_member_phone)}</td>
                  <td>
                    <button type="button" className="add-btn" onClick={() => startEdit(m)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => deleteMember(m.id)}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
